import styled from "@emotion/styled";
import { GrBox, GrButton, GrDialog, GrStack, palette, typography } from "@jnpmedi/grebob";
import SignaturePad from "react-signature-pad-wrapper";
import { DrawingToolbar } from "@newComponents/common/DrawingToolbar";
import { defaultMarkerColor, getColorBySignerIndex } from "@newComponents/common/Viewer/types";
import trimCanvas from "@newLibs/canvas/trimCanvas";
import { MvnMessage } from "@newLibs/utils";
import { UseBoolean, useBoolean } from "@newHooks/common/useBoolean";
import { useDrawingRefTrace } from "@newHooks/common/useDrawingRefTrace";
import { useDocumentMarkerContext } from "@newHooks/context/document/documentMarker.context";
import { notify } from "@newHooks/context/snackbar/notify";
import { fadeInBorderMarker } from "@assets/styles/animations";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { baseWidth } from "@assets/theme/baseSize/baseWidth";
import { EConsentMarkerProps } from "./EConsentMarker";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { DocumentSignerAttrType } from "@maven-rest/docs/DocumentSign.dto";

const FOLLOW_WRITE_PAD_IMAGE_MAX_WIDTH = 96;
const FOLLOW_WRITE_PAD_IMAGE_MAX_HEIGHT = 28;

export function FollowWritingMarker(props: EConsentMarkerProps) {
  const { markers, setMarkers } = useDocumentMarkerContext();

  const isFollowWritingDialogOpen = useBoolean(false);

  const getMarkerType = (attrType: string) => {
    switch (attrType) {
      case DocumentSignerAttrType.AgentFollowWritingItemMark:
        return {
          index: DocumentSignerAttrType.AgentFollowWritingItemIndex,
          missing: DocumentSignerAttrType.AgentFollowWritingItemMissing,
          value: DocumentSignerAttrType.AgentFollowWritingItemValue,
          answer: DocumentSignerAttrType.AgentFollowWritingItemAnswer,
        };
      default:
        return {
          index: DocumentSignerAttrType.FollowWritingItemIndex,
          missing: DocumentSignerAttrType.FollowWritingItemMissing,
          value: DocumentSignerAttrType.FollowWritingItemValue,
          answer: DocumentSignerAttrType.FollowWritingItemAnswer,
        };
    }
  };
  const markerTypes = getMarkerType(props.marker.attr);

  const followWritingItemIndex = markers.find((e) => e.attr === markerTypes.index && e.attrUuid === props.marker.groupUuid);
  const followWritingItemValue = markers.find((e) => e.attr === markerTypes.value && followWritingItemIndex?.attrUuid === e.groupUuid);
  const followWritingItemAnswer = markers.find((e) => e.attr === markerTypes.answer && followWritingItemIndex?.attrUuid === e.groupUuid);

  const handleFollowWritingValueChange = (newValue: string) => {
    const updatedMarkers = markers
      .filter(
        (x) =>
          !(
            x.attr === markerTypes.value &&
            x.attrUuid === followWritingItemValue?.attrUuid &&
            x.groupUuid === followWritingItemValue?.groupUuid
          )
      )
      .concat([
        {
          id: followWritingItemValue?.id,
          signerUuid: followWritingItemValue?.signerUuid!,
          target: followWritingItemValue?.target!,
          signerIndex: followWritingItemValue?.signerIndex!,
          attr: markerTypes.value,
          attrUuid: followWritingItemValue?.attrUuid!,
          groupUuid: followWritingItemValue?.groupUuid!,
          value: newValue,
          page: followWritingItemValue?.page!,
          x: followWritingItemValue?.x!,
          y: followWritingItemValue?.y!,
        },
      ]);
    setMarkers(updatedMarkers);
  };

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (props.isModeSigning) {
      props.onClick && props.onClick(e);
      isFollowWritingDialogOpen.onTrue();
    }
  };

  return (
    <>
      <StyledFollowWritingWrapper {...props} isAnswered={!!followWritingItemValue?.value} isHidden={props.isHidden} onClick={handleOnClick}>
        {props.isModeSigning ? (
          followWritingItemValue?.value ? (
            <img src={followWritingItemValue.value} style={{ width: "100%", height: "100%", objectFit: "contain" }} alt="따라쓰기" />
          ) : (
            followWritingItemAnswer?.value
          )
        ) : (
          props.children
        )}
      </StyledFollowWritingWrapper>
      {isFollowWritingDialogOpen.value && (
        <FollowWritingDialog
          open={isFollowWritingDialogOpen}
          onConfirm={(value) => {
            handleFollowWritingValueChange(value);
            isFollowWritingDialogOpen.onFalse();
          }}
          onCancel={() => isFollowWritingDialogOpen.onFalse()}
          followWritingItemValue={followWritingItemAnswer?.value || ""}
        />
      )}
    </>
  );
}

const StyledFollowWritingWrapper = styled("div")<EConsentMarkerProps & { isAnswered: boolean }>((props) => ({
  userSelect: "none",
  boxSizing: "border-box",
  display: props.isHidden ? "none" : "flex",
  position: "absolute",
  top: props.top,
  left: props.left,
  transform: "translateY(-50%)",
  borderRadius: "2px",
  border:
    props.isModeSigning || props.isAnswered
      ? `2px solid ${palette[defaultMarkerColor.additional.border]}`
      : `2px solid ${palette[getColorBySignerIndex(props.marker.signerIndex).border]}`,

  width: `${baseWidth.appMarkerSignatureWidth} !important`,
  height: `${baseHeight.appMarkerSignatureHeight} !important`,
  padding: props.isAnswered ? 0 : 1,
  outline: "none",

  alignItems: "center",
  justifyContent: "center",

  backgroundColor: props.isAnswered
    ? "transparent"
    : props.isModeSigning
    ? defaultMarkerColor.sign.background
    : getColorBySignerIndex(props.marker.signerIndex).background,

  color: "#878d96",
  fontSize: "10px",
  fontWeight: 500,
  lineHeight: "135%",
  whiteSpace: "nowrap",
  textIndent: 0,
  cursor: props.isModeSigning || props.isAnswered ? "pointer" : "default",
  "&:hover": {
    backgroundColor:
      props.isModeSigning || props.isAnswered
        ? defaultMarkerColor.sign.backgroundHover
        : getColorBySignerIndex(props.marker.signerIndex).backgroundHover,

    "& > span": {
      opacity: 1,
    },
  },

  "&":
    !props.isAnswered && props.isModeSigning
      ? {
          animation: `${fadeInBorderMarker} 1.6s ease-in-out infinite`,
        }
      : "",

  "& > select": {
    fontSize: "10px",
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
  },
}));

interface FollowWritingDialogProps {
  open: UseBoolean;
  onConfirm: (value: string) => void;
  onCancel: () => void;
  followWritingItemValue: string;
}

function FollowWritingDialog({ open, onConfirm, onCancel, followWritingItemValue }: FollowWritingDialogProps) {
  const { signaturePadRef, isSignatureEmpty, isRedoStackEmpty, onClickUndo, onClickRedo, onClickClear } = useDrawingRefTrace();

  const handleConfirm = () => {
    const isSignatureImageEmpty = signaturePadRef.current?.isEmpty();

    if (typeof isSignatureImageEmpty !== "boolean" || isSignatureImageEmpty) {
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_ECONSENT_FOLLOW_WRITING_FIELD_MISSING), "WARN");
      return;
    }

    const canvas = signaturePadRef.current?.canvas.current!;
    const canvasCopy = document.createElement("canvas");
    canvasCopy.width = canvas.width;
    canvasCopy.height = canvas.height;
    canvasCopy.getContext("2d")!.drawImage(canvas, 0, 0);

    const signatureImage = trimCanvas(canvasCopy);

    if (!signatureImage) {
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_ECONSENT_FOLLOW_WRITING_FIELD_FAILED_IDENTIFY_SIGNATURE), "WARN");
      onClickClear();

      return;
    }

    let width = signatureImage.width;
    let height = signatureImage.height;

    // 비율을 유지하면서 최대 크기 적용
    const widthRatio = FOLLOW_WRITE_PAD_IMAGE_MAX_WIDTH / width;
    const heightRatio = FOLLOW_WRITE_PAD_IMAGE_MAX_HEIGHT / height;
    const ratio = Math.min(widthRatio, heightRatio);

    if (ratio < 1) {
      width = width * ratio;
      height = height * ratio;
    }

    const signatureData = signatureImage?.toDataURL("image/png") || "";
    onConfirm(signatureData);
  };

  return (
    <GrDialog
      title={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_ECONSENT_FOLLOW_WRITING_FIELD_TITLE)}
      open={open.value}
      size="xs"
      footer={
        <GrButton variant="outlined" color="grey" startIcon="eraser" onClick={onClickClear}>
          {MvnMessage(COMMON_LANG.CLEAR)}
        </GrButton>
      }
      confirmButton={{ children: MvnMessage(COMMON_LANG.OK) }}
      onConfirm={handleConfirm}
      cancelButton={{ children: MvnMessage(COMMON_LANG.CANCEL) }}
      onClose={onCancel}
      preventCloseOnOutsideClick
    >
      <GrStack flexDirection="column" gap={8}>
        <SignatureStackWrapper
          key="drawSignature"
          border={`1px solid ${palette["grey025"]}`}
          alignItems="center"
          justifyContent="center"
          borderRadius="8px"
          width="100%"
          height={baseHeight.appSignaturePadHeight}
        >
          <SignaturePadWrapper followText={followWritingItemValue}>
            <SignaturePad
              ref={signaturePadRef}
              options={{
                penColor: "black",
                minWidth: 3,
                maxWidth: 5,
                dotSize: 3,
              }}
              canvasProps={{
                className: "app-docs-document--signature-canvas",
                width: "100%",
                height: baseHeight.appSignaturePadHeight,
              }}
            />
          </SignaturePadWrapper>
        </SignatureStackWrapper>

        <DrawingToolbar
          undoDisabled={isSignatureEmpty}
          redoDisabled={isRedoStackEmpty}
          onClickRedo={onClickRedo}
          onClickUndo={onClickUndo}
        />
      </GrStack>
    </GrDialog>
  );
}

const SignatureStackWrapper = styled(GrStack)`
  position: relative;
  box-sizing: border-box;
`;

const SignaturePadWrapper = styled(GrBox)<{ followText: string }>(({ followText }) => ({
  width: "100%",
  height: "100%",

  "&:before": {
    content: `"${followText}"`,
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: `calc(${baseHeight.appSignaturePadHeight} - 2px)`,
    borderRadius: "7px",
    background: "#f4f6f9",
    color: "#697077",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textIndent: 0,
    pointerEvents: "none",
    ...typography["Content.Subtitle04"],
  },

  ".app-docs-document--signature-canvas": {
    position: "relative",
    width: "100%",
    height: "100%",
  },
}));
